import React, { useState, useEffect, useCallback } from 'react';
import PageHeader from '../components/PageHeader';
import PageLoading from '../components/PageLoading';
import { useFailedMutations } from '../hooks/useFailedMutations';
import { retryMutation, discardMutation } from '../services/mutationStore';
import { triggerDrain } from '../services/sync';
import { db } from '../db';

const MUTATION_LABELS = {
  mark_read: 'Mark read',
  mark_unread: 'Mark unread',
  mark_all_read: 'Mark all read',
  feedback: 'Feedback',
  highlight_create: 'Create highlight',
  highlight_delete: 'Delete highlight',
  digest_highlight_create: 'Create digest highlight',
};

function formatTarget(m, titleMap) {
  const p = m.payload ?? {};
  if (p.article_id != null) {
    return titleMap[p.article_id] ?? `Article #${p.article_id}`;
  }
  if (Array.isArray(p.article_ids)) {
    return `${p.article_ids.length} articles in topic ${p.topic_id ?? ''}`.trim();
  }
  return '—';
}

export default function PendingIssues() {
  const { mutations, isLoading } = useFailedMutations();
  const [titleMap, setTitleMap] = useState({});
  const [busy, setBusy] = useState({});

  useEffect(() => {
    if (!mutations.length) { setTitleMap({}); return; }
    const ids = [...new Set(
      mutations.flatMap(m => {
        if (m.payload?.article_id != null) return [m.payload.article_id];
        if (Array.isArray(m.payload?.article_ids)) return m.payload.article_ids;
        return [];
      })
    )];
    db.table('articles').bulkGet(ids).then(rows => {
      const map = {};
      rows.forEach((r, i) => {
        if (r) map[ids[i]] = r.title ?? r.payload?.title ?? null;
      });
      setTitleMap(map);
    }).catch(() => {});
  }, [mutations]);

  const handleRetry = useCallback(async (key) => {
    setBusy(b => ({ ...b, [key]: 'retry' }));
    try {
      await retryMutation(key);
      triggerDrain();
    } finally {
      setBusy(b => { const n = { ...b }; delete n[key]; return n; });
    }
  }, []);

  const handleDiscard = useCallback(async (key) => {
    setBusy(b => ({ ...b, [key]: 'discard' }));
    try {
      await discardMutation(key);
    } finally {
      setBusy(b => { const n = { ...b }; delete n[key]; return n; });
    }
  }, []);

  const handleRetryAll = useCallback(async () => {
    for (const m of mutations) await retryMutation(m.key);
    triggerDrain();
  }, [mutations]);

  const handleDiscardAll = useCallback(async () => {
    for (const m of mutations) await discardMutation(m.key);
  }, [mutations]);

  if (isLoading) {
    return <PageLoading message="Loading pending issues…" />;
  }

  return (
    <div className="min-h-screen bg-clay-bg">
      <PageHeader
        emoji="⚠️"
        title="Pending Issues"
        subtitle={`${mutations.length} failed sync${mutations.length !== 1 ? 's' : ''}`}
        backTo="/dashboard"
        backLabel="Back"
        actions={mutations.length > 0 && (
          <>
            <button onClick={handleDiscardAll} className="btn-clay-secondary btn-clay-sm">
              Discard all
            </button>
            <button onClick={handleRetryAll} className="btn-clay btn-clay-sm">
              Retry all
            </button>
          </>
        )}
      />

      {/* Content */}
      <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-8">
        {mutations.length === 0 ? (
          <div className="card-clay-static text-center py-16">
            <p className="text-4xl mb-4" role="img" aria-label="All clear">✅</p>
            <h2 className="font-display text-xl font-semibold text-heading mb-2">All caught up</h2>
            <p className="text-secondary-text">No pending sync issues.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {mutations.map(m => {
              const isBusy = !!busy[m.key];
              return (
                <div key={m.key} className="card-clay-static p-4 flex items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="badge-fun-coral text-xs">
                        {MUTATION_LABELS[m.type] ?? m.type}
                      </span>
                      <span className="text-sm text-heading truncate">
                        {formatTarget(m, titleMap)}
                      </span>
                    </div>
                    {m.last_error && (
                      <p className="text-xs text-secondary-text truncate">{m.last_error}</p>
                    )}
                    <p className="text-xs text-tertiary-text mt-1">
                      {new Date(m.created_at).toLocaleString()}
                      {' · '}{m.attempts ?? 0} attempts
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleDiscard(m.key)}
                      disabled={isBusy}
                      className="btn-clay-ghost btn-clay-sm text-xs"
                    >
                      {busy[m.key] === 'discard' ? '…' : 'Discard'}
                    </button>
                    <button
                      onClick={() => handleRetry(m.key)}
                      disabled={isBusy}
                      className="btn-clay-secondary btn-clay-sm text-xs"
                    >
                      {busy[m.key] === 'retry' ? '…' : 'Retry'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
